import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getBrokerages, 
  getBrokerage, 
  getBrokerageLocations,
  submitBrokerageLead,
  getAdminBrokerages,
  createBrokerage,
  updateBrokerage,
  createBrokerageLocation,
  updateBrokerageLocation,
  deleteBrokerageLocation,
} from '@/data/brokerages';
import type { 
  BrokerageFilters, 
  BrokerageLeadInput,
  Brokerage,
  BrokerageLocation,
} from '@/contracts/brokerage';
import { toast } from 'sonner';

/**
 * Hook for fetching filtered brokerages list
 */
export function useBrokerages(filters: BrokerageFilters) {
  return useQuery({
    queryKey: ['brokerages', filters],
    queryFn: () => getBrokerages(filters),
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook for fetching a single brokerage by slug
 */
export function useBrokerage(slug: string | undefined) {
  return useQuery({
    queryKey: ['brokerage', slug],
    queryFn: () => getBrokerage(slug!),
    enabled: !!slug,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook for fetching brokerage locations
 */
export function useBrokerageLocations(
  brokerageId: string | undefined,
  options?: {
    state?: string;
    lat?: number;
    lng?: number;
    limit?: number;
  }
) {
  return useQuery({
    queryKey: ['brokerage-locations', brokerageId, options],
    queryFn: () => getBrokerageLocations(brokerageId!, options),
    enabled: !!brokerageId,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook for submitting a brokerage lead
 */
export function useSubmitBrokerageLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (lead: BrokerageLeadInput) => submitBrokerageLead(lead),
    onSuccess: () => {
      toast.success('Your request has been submitted! The brokerage will be in touch soon.');
    },
    onError: (error) => {
      console.error('Failed to submit lead:', error);
      toast.error('Failed to submit your request. Please try again.');
    },
  });
}

// Admin hooks

/**
 * Hook for fetching all brokerages (admin)
 */
export function useAdminBrokerages() {
  return useQuery({
    queryKey: ['admin-brokerages'],
    queryFn: getAdminBrokerages,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook for creating a brokerage (admin)
 */
export function useCreateBrokerage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (brokerage: Partial<Brokerage>) => createBrokerage(brokerage),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-brokerages'] });
      queryClient.invalidateQueries({ queryKey: ['brokerages'] });
      toast.success('Brokerage created successfully');
    },
    onError: (error) => {
      console.error('Failed to create brokerage:', error);
      toast.error('Failed to create brokerage');
    },
  });
}

/**
 * Hook for updating a brokerage
 */
export function useUpdateBrokerage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Brokerage> }) => 
      updateBrokerage(id, updates),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-brokerages'] });
      queryClient.invalidateQueries({ queryKey: ['brokerages'] });
      queryClient.invalidateQueries({ queryKey: ['brokerage', data.slug] });
      toast.success('Brokerage updated successfully');
    },
    onError: (error) => {
      console.error('Failed to update brokerage:', error);
      toast.error('Failed to update brokerage');
    },
  });
}

/**
 * Hook for creating a brokerage location
 */
export function useCreateBrokerageLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (location: Partial<BrokerageLocation>) => createBrokerageLocation(location),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['brokerage-locations', data.brokerage_id] });
      toast.success('Location added successfully');
    },
    onError: (error) => {
      console.error('Failed to create location:', error);
      toast.error('Failed to add location');
    },
  });
}

/**
 * Hook for updating a brokerage location
 */
export function useUpdateBrokerageLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<BrokerageLocation> }) =>
      updateBrokerageLocation(id, updates),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['brokerage-locations', data.brokerage_id] });
      toast.success('Location updated successfully');
    },
    onError: (error) => {
      console.error('Failed to update location:', error);
      toast.error('Failed to update location');
    },
  });
}

/**
 * Hook for deleting a brokerage location
 */
export function useDeleteBrokerageLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, brokerageId }: { id: string; brokerageId: string }) => 
      deleteBrokerageLocation(id).then(() => brokerageId),
    onSuccess: (brokerageId) => {
      queryClient.invalidateQueries({ queryKey: ['brokerage-locations', brokerageId] });
      toast.success('Location deleted successfully');
    },
    onError: (error) => {
      console.error('Failed to delete location:', error);
      toast.error('Failed to delete location');
    },
  });
}
